'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);

  const navigationItems: Array<
    | { label: string; href: string; icon: string }
    | { label: string; icon: string; children: Array<{ label: string; href: string }> }
  > = [
    { label: 'Laman Utama', href: '/homepage', icon: 'HomeIcon' },
    { label: 'Direktori Ejen', href: '/agent-directory', icon: 'UsersIcon' },
    {
      label: 'Maklumat Servis',
      icon: 'InformationCircleIcon',
      children: [
        { label: 'Tips & Inspirasi', href: '/maklumat-servis/tips' },
        { label: 'Servis Lipat', href: '/maklumat-servis/servis-lipat' },
        { label: 'Servis Gosok', href: '/maklumat-servis/servis-gosok' },
      ],
    },
    { label: 'Soalan Lazim', href: '/faq-page', icon: 'QuestionMarkCircleIcon' },
    { label: 'Hubungi Kami', href: '/contact-page', icon: 'PhoneIcon' },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className={`bg-card shadow-md fixed top-0 left-0 right-0 z-50 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link
            href="/homepage"
            className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-300"
          >
            <div className="relative w-10 h-10">
              <Image
                src="/assets/Surilipat%20logo.jpg"
                alt="Surilipat Logo"
                width={40}
                height={40}
                className="rounded-lg object-cover"
              />
            </div>
            <span className="text-xl font-heading font-bold text-primary">
              Surilipat<span className="text-accent">.hub</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item, idx) => {
              if ('children' in item) {
                return (
                  <div key={`desktop-services-${idx}`} className="relative">
                    <button
                      onClick={() => setIsServicesOpen((prev) => !prev)}
                      className="flex items-center space-x-2 px-4 py-2 rounded-md text-textSecondary hover:text-primary hover:bg-muted transition-all duration-300 font-body font-medium"
                      aria-haspopup="menu"
                      aria-expanded={isServicesOpen}
                    >
                      <Icon name={item.icon as any} size={20} variant="outline" />
                      <span>{item.label}</span>
                      <Icon
                        name={isServicesOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                        size={16}
                        variant="outline"
                        className="ml-1"
                      />
                    </button>
                    {isServicesOpen && (
                      <div className="absolute left-0 top-full mt-2 min-w-[220px] rounded-lg border border-border bg-card shadow-xl p-2">
                        {item.children.map((child) => (
                          <Link
                            key={`desktop-${child.href}`}
                            href={child.href}
                            onClick={() => setIsServicesOpen(false)}
                            className="block px-3 py-2 rounded-md text-textSecondary hover:text-primary hover:bg-muted transition-all duration-300 font-body"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={`desktop-${item.href}`}
                  href={item.href}
                  className="flex items-center space-x-2 px-4 py-2 rounded-md text-textSecondary hover:text-primary hover:bg-muted transition-all duration-300 font-body font-medium"
                >
                  <Icon name={item.icon as any} size={20} variant="outline" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center space-x-3">
            <Link
              href="/agent-registration"
              className="px-6 py-2.5 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-cta font-semibold hover:shadow-brand transition-all duration-300 transform hover:scale-105"
            >
              Jadi Ejen
            </Link>
          </div>

          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-md text-textSecondary hover:text-primary hover:bg-muted transition-colors duration-300"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} variant="outline" />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border">
          <nav className="container mx-auto px-4 py-4 space-y-2">
            {navigationItems.map((item, idx) => {
              if ('children' in item) {
                return (
                  <div key={`mobile-services-${idx}`} className="space-y-1">
                    <button
                      onClick={() => setIsServicesOpen((prev) => !prev)}
                      className="flex w-full items-center justify-between px-4 py-3 rounded-lg text-textSecondary hover:text-primary hover:bg-muted transition-all duration-300 font-body font-medium"
                      aria-haspopup="menu"
                      aria-expanded={isServicesOpen}
                    >
                      <span className="flex items-center gap-2">
                        <Icon name={item.icon as any} size={20} variant="outline" />
                        {item.label}
                      </span>
                      <Icon
                        name={isServicesOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                        size={18}
                        variant="outline"
                      />
                    </button>
                    {isServicesOpen && (
                      <div className="pl-10 space-y-2">
                        {item.children.map((child) => (
                          <Link
                            key={`mobile-${child.href}`}
                            href={child.href}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="block px-4 py-2 rounded-md text-textSecondary hover:text-primary hover:bg-muted transition-all duration-300 font-body"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={`mobile-${(item as any).href}`}
                  href={(item as any).href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg text-textSecondary hover:text-primary hover:bg-muted transition-all duration-300 font-body font-medium"
                >
                  <Icon name={(item as any).icon} size={20} variant="outline" />
                  <span>{(item as any).label}</span>
                </Link>
              );
            })}
            <Link
              href="/agent-registration"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-cta font-semibold hover:shadow-brand transition-all duration-300 mt-4"
            >
              <Icon name="UserPlusIcon" size={20} variant="outline" />
              <span>Jadi Ejen</span>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
